import {
  Box, Button, Checkbox, Pagination, Typography, useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'
import { de, enGB } from 'date-fns/locale'
import {
  MouseEvent, ReactNode, useEffect, useMemo, useState
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { TOKENS } from '../../../../pages/dashboard-v2/components/primitives'
import { FolderChecklist, FolderTemplate } from '../../../../types/FolderChecklist'
import { IDataTableAction } from '../../../widgets/DataTableHeader'
import {
  AssigneesCell, Chip, DateCell, TitleCell
} from './NewDesignCells'
import { FolderChips, headerButton, RowMenu } from './ProcessListV2'

// The "Vorlagen" tab of the new design (redesign round 1), built like the process list: folders as
// chips, one row per template with what it is (questions, projects, schedules), who works with it,
// when it was last used and how many of its processes are open, and "Starten" right in the row.
// Data, actions, selection, sorting and paging come from Folder.tsx exactly as for the old table.

interface MultiSelectAction {
  label: string
  callback: (items: any[]) => void
  show?: boolean
  isDanger?: boolean
}

interface Props {
  rows: any[]
  raw: FolderTemplate[]
  offlineIds: string[]
  actions: Array<IDataTableAction<any>>
  multiSelectActions: MultiSelectAction[]
  sortColumn: string
  sortDir: string
  onSort: (field: string, direction: 'asc' | 'desc') => void
  page: number
  totalPages: number
  onPagination: (page: number) => void
  onOpenFolder: (id: string) => void
  isLoading: boolean
  // editor for people who may edit templates (desktop, online), else the start page
  linkOf: (x: FolderTemplate) => string
  breadcrumb?: ReactNode
  toolbar?: ReactNode
}

function toDate(value?: string | null) {
  if (!value) return null
  const date = parseISO(String(value))
  return isValid(date) ? date : null
}

export default function TemplateListV2(props: Props) {
  const {
    rows, raw, offlineIds, actions, multiSelectActions, sortColumn, sortDir, onSort, page, totalPages,
    onPagination, onOpenFolder, isLoading, linkOf, breadcrumb, toolbar
  } = props
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const theme = useTheme()
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [tabSlot, setTabSlot] = useState<HTMLElement | null>(null)
  useEffect(() => { setTabSlot(document.getElementById('nd-tab-actions')) }, [])
  const [selected, setSelected] = useState<string[]>([])
  useEffect(() => { setSelected([]) }, [raw])
  const locale = i18n.language?.startsWith('de') ? de : enGB

  const folders = useMemo(() => raw
    .map((x, index) => ({ x: x as unknown as FolderChecklist, row: rows[index] }))
    .filter(({ x }) => x.entity_type === 'folder')
    .sort((a, b) => String(a.x.title ?? '').localeCompare(String(b.x.title ?? ''), 'de', { numeric: true, sensitivity: 'base' })),
  [raw, rows])
  const templates = useMemo(() => raw
    .map((x, index) => ({ x, row: rows[index] }))
    .filter(({ x }) => x.entity_type !== 'folder'), [raw, rows])

  const toggle = (id: string) => setSelected((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  const selectedRows = rows.filter((r) => selected.includes(r.id))
  const allIds = templates.map(({ x }) => x.id)
  const sortBy = (field: string) => onSort(field, sortColumn === field && sortDir === 'asc' ? 'desc' : 'asc')
  const sortMark = (field: string) => (sortColumn === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '')

  const columns = 'minmax(0,1fr) 150px 170px 120px 110px 44px'
  const gridColumns = `28px ${columns}`

  // "24 Fragen · Audit · Projekt A, Projekt B · 2 Pläne"
  const subOf = (x: FolderTemplate) => [
    x.stats ? t('templateList.questions', { count: x.stats.question_count }) : null,
    x.is_audit ? t('templateList.audit') : null,
    (x.projects ?? []).map((p) => p.name).join(', ') || null,
    x.schedule_count ? t('templateList.schedules', { count: x.schedule_count }) : null
  ].filter(Boolean).join(' · ')

  const people = (x: FolderTemplate) => (
    <AssigneesCell
      assignees={x.people}
      // nobody assigned: the creator, dashed like on the process list
      responsible={x.people?.length ? null : { source: 'templateCreator', people: x.assignees ?? [] }}
    />
  )

  const lastUsed = (x: FolderTemplate) => {
    // backends before 1.49.3 send no figures: empty instead of a wrong "never used"
    if (!x.stats) return <span />
    const date = toDate(x.stats.last_used_at)
    if (!date) return <Typography sx={{ fontSize: 13, color: TOKENS.muted }}>{t('templateList.neverUsed')}</Typography>
    return (
      <Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{formatDistanceToNowStrict(date, { addSuffix: true, locale })}</Typography>
        {!!x.stats?.open_count && (
          <Typography sx={{ fontSize: 12, color: TOKENS.muted }}>{t('templateList.open', { count: x.stats.open_count })}</Typography>
        )}
      </Box>
    )
  }

  const startButton = (x: FolderTemplate, full = false) => (
    <Button
      variant="outlined"
      size="small"
      onClick={(e: MouseEvent) => { e.stopPropagation(); history.push(`/resultset/${x.id}/new`) }}
      startIcon={<i className="fas fa-play" style={{ fontSize: 10 }} />}
      sx={{
        height: 34, px: 1.5, borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', bgcolor: '#FFFFFF', width: full ? '100%' : 'auto'
      }}
    >
      {t('templateList.start')}
    </Button>
  )

  const titleOf = (x: FolderTemplate) => (
    <TitleCell
      title={x.title}
      to={linkOf(x)}
      sub={subOf(x)}
      offline={offlineIds.includes(x.id)}
      extra={x.draft ? <Box sx={{ mt: 0.5 }}><Chip tone="warning">{t('checklist.labels.draft')}</Chip></Box> : null}
    />
  )

  const renderRow = ({ x, row }: { x: FolderTemplate, row: any }) => {
    const open = () => history.push(linkOf(x))
    if (isPhone) {
      return (
        <Box
          key={x.id}
          onClick={open}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.75, bgcolor: '#FFFFFF', border: '1px solid #C3CCD8',
            borderRadius: '12px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(16,24,40,.10)'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{titleOf(x)}</Box>
            <Box onClick={(e) => e.stopPropagation()}><RowMenu row={row} actions={actions} /></Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>{lastUsed(x)}</Box>
            {startButton(x)}
          </Box>
        </Box>
      )
    }
    return (
      <Box
        key={x.id}
        onClick={open}
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: 2,
          alignItems: 'center',
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${TOKENS.divider}`,
          bgcolor: '#FFFFFF',
          cursor: 'pointer',
          '&:hover': { bgcolor: '#F7F9FC' }
        }}
      >
        <Checkbox
          size="small"
          sx={{ p: 0 }}
          checked={selected.includes(x.id)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggle(x.id)}
          inputProps={{ 'aria-label': t('processList.select', { title: x.title }) }}
        />
        {titleOf(x)}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>{people(x)}</Box>
        {lastUsed(x)}
        <DateCell value={x.created_at} />
        <Box>{startButton(x)}</Box>
        <Box onClick={(e) => e.stopPropagation()}><RowMenu row={row} actions={actions} /></Box>
      </Box>
    )
  }

  const header = (
    <Box sx={{
      display: { xs: 'none', md: 'grid' },
      gridTemplateColumns: gridColumns,
      gap: 2,
      alignItems: 'center',
      px: 2.5,
      py: 1.25,
      bgcolor: '#F7F9FC',
      fontSize: 12,
      fontWeight: 600,
      color: TOKENS.muted
    }}
    >
      <Checkbox
        size="small"
        sx={{ p: 0 }}
        inputProps={{ 'aria-label': t('processList.selectAll') }}
        checked={allIds.length > 0 && allIds.every((id) => selected.includes(id))}
        indeterminate={selected.length > 0 && !allIds.every((id) => selected.includes(id))}
        onChange={(e) => setSelected(e.target.checked ? allIds : [])}
      />
      <Box component="button" type="button" onClick={() => sortBy('title')} sx={headerButton}>{t('templateList.template')}{sortMark('title')}</Box>
      <span>{t('templateList.people')}</span>
      <span>{t('templateList.lastUsed')}</span>
      <Box component="button" type="button" onClick={() => sortBy('created_at')} sx={headerButton}>{t('templateList.created')}{sortMark('created_at')}</Box>
      <span />
      <span />
    </Box>
  )

  const visibleBulk = multiSelectActions.filter((a) => a.show !== false)
  const toolbarInTabs = !!toolbar && !!tabSlot && isDesktop
  const showHeadRow = folders.length > 0 || (!!toolbar && !toolbarInTabs)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {breadcrumb}
      {toolbarInTabs && createPortal(toolbar, tabSlot as HTMLElement)}
      {showHeadRow && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minHeight: 40
        }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {folders.length > 0 && (
              <FolderChips
                folders={folders}
                closed={false}
                actions={actions}
                onOpenFolder={onOpenFolder}
                count={(f) => Number(f.template_count ?? 0)}
                phone={isPhone}
              />
            )}
          </Box>
          {toolbar && !toolbarInTabs && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 'none' }}>{toolbar}</Box>
          )}
        </Box>
      )}

      {selected.length > 0 && visibleBulk.length > 0 && (
        <Box sx={{
          display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', p: 1.25, borderRadius: '10px', bgcolor: TOKENS.primarySoft
        }}
        >
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, mr: 1 }}>{t('processList.selected', { count: selected.length })}</Typography>
          {visibleBulk.map((a) => (
            <Button
              key={a.label}
              size="small"
              variant="outlined"
              color={a.isDanger ? 'error' : 'primary'}
              onClick={() => a.callback(selectedRows)}
              sx={{ textTransform: 'none', bgcolor: '#FFFFFF' }}
            >
              {a.label}
            </Button>
          ))}
          <Button size="small" onClick={() => setSelected([])} sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
        </Box>
      )}

      {isPhone ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{templates.map(renderRow)}</Box>
      ) : templates.length > 0 && (
        <Box sx={{
          bgcolor: '#FFFFFF', border: '1px solid #C3CCD8', borderRadius: '12px', overflow: 'hidden', boxShadow: TOKENS.cardShadow
        }}
        >
          {header}
          {templates.map(renderRow)}
        </Box>
      )}

      {!isLoading && templates.length === 0 && (
        <Typography sx={{ color: TOKENS.muted, fontSize: 14, py: 3, textAlign: 'center' }}>
          {folders.length ? t('templateList.emptyHere') : t('datatable.noData')}
        </Typography>
      )}

      {!isPhone && totalPages > 1 && (
        <Pagination page={page} count={totalPages} onChange={(_, value) => onPagination(value)} shape="rounded" />
      )}
    </Box>
  )
}
